import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { BlueButton, Box } from '~Styles';
import { Input } from '~Styles/inputs';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { useButton, useI18n, useLogger } from '~Hooks';
import getPasswordStrength from './getPasswordStrength';

import bLogo from '~Assets/Logos/b_logo_black.svg';
import { ChangeTempPassword, ChangeTempPasswordVariables } from './__queries__';
import { handyBreakpoint } from '~Styles/breakpoints';

const CHANGE_PASSWORD = gql`
  mutation ChangeTempPassword($newPassword: String!) {
    changeTempPassword(newPassword: $newPassword) {
      success
      token
      message
      error
    }
  }
`;

const DEV = process.env.NODE_ENV === 'development';

/**
 * Whether we are pointed at the dev backend, regardless of how this bundle was
 * built. A production build against the dev API is the normal way to test on a
 * real device, and there is nothing to protect there.
 */
const DEV_API = (process.env.FEDERATION_URL ?? '').includes('dev');

/** No length, character or strength rules against a dev backend. */
const needCheck = !DEV && !DEV_API;
const minimumLength = 8;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: 100vh;
  width: 100%;
  position: fixed;
  z-index: 99;
`;

const FormSide = styled(Box)`
  animation: ${fadeIn} 0.3s ease-in-out 0.1s forwards;
  opacity: 0;
  margin-bottom: 10%;
  width: 400px;

  ${handyBreakpoint} {
    margin-bottom: auto;
  }
`;

const BLogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  filter: invert(1);
`;

const FormContainer = styled.form`
  flex: 1;
  max-width: 400px;
  min-width: 272px;
  width: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const InfoBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorBlueGray200) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorBlueGray200) 20%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
`;

const SubmitButton = styled(BlueButton)`
  max-height: 40px;
`;

interface Props {
  onSuccess?: () => void;
}

const ResetPassword: FC<Props> = ({ onSuccess }) => {
  const { i18n } = useI18n();
  const logger = useLogger('reset-password');

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');

  const [changeTempPasswordMutation, changeTempPasswordPayload] = useMutation<
    ChangeTempPassword,
    ChangeTempPasswordVariables
  >(CHANGE_PASSWORD);

  // Password validation
  const passwordCheck = () => {
    if (!needCheck) {
      return true;
    }
    const passwordRegEx = /^[0-9A-Za-z!@#$%^&*?_~]+$/;
    let error = '';

    if (password !== password.trim()) {
      error = i18n('ResetPassword.Error.NoSpaces');
    } else if (password.length < minimumLength) {
      error = i18n('ResetPassword.Error.TooShort');
    } else if (!passwordRegEx.test(password)) {
      error = i18n('ResetPassword.Error.OnlyLetters');
    } else if (process.env.NODE_ENV === 'production' && getPasswordStrength(password) < 40) {
      error = i18n('ResetPassword.Error.NotStrong');
    }

    setPasswordError(error);
    return !error;
  };

  const confirmPasswordCheck = () => {
    if (password !== confirmPassword) {
      setConfirmPasswordError(i18n('ResetPassword.Error.Mismatch'));
      return false;
    }

    setConfirmPasswordError('');
    return true;
  };

  // Update password strength indicator
  const changePasswordStrength = () => {
    if (!needCheck) {
      return;
    }
    if (password.length === 0) {
      setPasswordStrength('');
    } else if (password.length < minimumLength) {
      setPasswordStrength(i18n('ResetPassword.Error.Short'));
    } else {
      let strength = i18n('ResetPassword.VeryWeak');
      const score = getPasswordStrength(password);

      if (score >= 80) {
        strength = i18n('ResetPassword.VeryStrong');
      } else if (score >= 60) {
        strength = i18n('ResetPassword.Strong');
      } else if (score >= 40) {
        strength = i18n('ResetPassword.Average');
      } else if (score >= 20) {
        strength = i18n('ResetPassword.Weak');
      }

      setPasswordStrength(strength);
    }
  };

  useEffect(() => {
    if (!password) {
      setPasswordError('');
      setPasswordStrength('');
    } else {
      changePasswordStrength();
      if (password && passwordError) {
        passwordCheck();
      }
    }
  }, [password]);

  useEffect(() => {
    if (confirmPasswordError) {
      confirmPasswordCheck();
    }
  }, [confirmPassword]);

  // Handle mutation success
  useEffect(() => {
    if (changeTempPasswordPayload.data?.changeTempPassword.success && onSuccess) {
      logger.info('Password changed successfully');
      onSuccess();
    }
  }, [changeTempPasswordPayload]);

  const handleChangePassword = () => {
    const isPasswordPassed = passwordCheck();
    const isConfirmationPasswordPassed = confirmPasswordCheck();

    if (isPasswordPassed && isConfirmationPasswordPassed) {
      logger.info('Attempting password change');
      changeTempPasswordMutation({ variables: { newPassword: password } });
    }
  };

  const submitButton = useButton({
    onClick: () => handleChangePassword(),
    disabled: changeTempPasswordPayload.loading,
  });

  const mutationError = changeTempPasswordPayload?.error?.message;

  return (
    <Wrapper display="flex" alignItems="center" justifyContent="center">
      <FormSide display="flex" flexDirection="column" padding="large">
        <FormContainer
          onSubmit={(e) => {
            e.preventDefault();
            handleChangePassword();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleChangePassword();
            }
          }}>
          <Box display="flex" flexDirection="column" alignItems="center" marginBottom="xs">
            <BLogoImg src={bLogo} alt="Baemingo" />
            <Heading level={2} color="var(--colorWhite)" top={12}>
              {i18n('ResetPassword.Title')}
            </Heading>
            <Paragraph level={5} color="var(--colorBlueGray200)" top={8}>
              {i18n('ResetPassword.Description')}
            </Paragraph>
          </Box>

          <Input
            dark
            type="password"
            placeholder={i18n('ResetPassword.NewPassword')}
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            onBlur={() => {
              if (password) passwordCheck();
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            autoFocus
          />

          {/* Password strength & requirements info */}
          {needCheck && password.length > 0 && (
            <InfoBox>
              {passwordStrength && (
                <Paragraph level={5} color="var(--colorBlueGray200)">
                  {i18n('ResetPassword.PasswordStrength')} {passwordStrength}
                </Paragraph>
              )}
              <Paragraph level={5} color="var(--colorBlueGray200)">
                {i18n('ResetPassword.Info')}
              </Paragraph>
            </InfoBox>
          )}

          <Input
            dark
            type="password"
            placeholder={i18n('ResetPassword.ConfirmPassword')}
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfirmPassword(e.target.value)
            }
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />

          <SubmitButton
            as="button"
            type="submit"
            fluid
            size="small"
            {...submitButton}
            marginTop="xs">
            {changeTempPasswordPayload.loading ? (
              <Loader size="sm" color="var(--colorWhite)" />
            ) : (
              i18n('ResetPassword.ChangePassword')
            )}
          </SubmitButton>

          {/* Password field error */}
          {passwordError && (
            <ErrorBox display="flex" alignItems="center" gap="xs">
              <Paragraph level={5} color="var(--colorUIRed)">
                {passwordError}
              </Paragraph>
            </ErrorBox>
          )}

          {/* Confirm password error */}
          {confirmPasswordError && (
            <ErrorBox display="flex" alignItems="center" gap="xs">
              <Paragraph level={5} color="var(--colorUIRed)">
                {confirmPasswordError}
              </Paragraph>
            </ErrorBox>
          )}

          {/* Mutation error */}
          {mutationError && (
            <ErrorBox display="flex" alignItems="center" gap="xs">
              <Paragraph level={5} color="var(--colorUIRed)">
                {mutationError}
              </Paragraph>
            </ErrorBox>
          )}
        </FormContainer>
      </FormSide>
    </Wrapper>
  );
};

export default ResetPassword;
